// Advanced list filters: the tree the server owns (`server/crm/filter_tree/tree.py`) and the
// schema it serves per model (`GET /crm/app/filter-schema/<app_label.model>/`).
// The front never compiles a tree: it edits it and sends it as is (JSON body).

export type FilterFieldType = "text" | "number" | "boolean" | "date" | "datetime" | "choice" | "picklist" | "record" | "geo";

export type FilterOperator =
    | "contains"
    | "not_contains"
    | "equals"
    | "not_equals"
    | "starts_with"
    | "ends_with"
    | "in"
    | "not_in"
    | "gt"
    | "gte"
    | "lt"
    | "lte"
    | "between"
    | "is_true"
    | "is_false"
    | "on"
    | "not_on"
    | "before"
    | "after"
    | "on_or_before"
    | "on_or_after"
    | "within_km"
    | "beyond_km"
    | "is_empty"
    | "is_not_empty";

export type Quantifier = "any" | "none" | "all" | "at_least" | "fewer_than";

/** `RelatedNode.relation` of a block probing every record of an object (`any:salesforce.sfopportunity`). */
export const ANY_RECORD_PREFIX = "any:";

export type ConditionNode = {
    type: "condition";
    id: string;
    /** Path of schema names: to-one relations, then a field (or a to-one relation itself). */
    field: string;
    operator: FilterOperator | "";
    value?: unknown[] | null;
    /** Display names of picked records, by id (`record` fields only). */
    value_labels?: Record<string, string> | null;
    /**
     * Compared with a formula of other fields instead of `value`: its canonical text
     * (`[$0.created_date] + 10 days`, see `server/crm/filter_tree/formula.py`).
     */
    formula?: string | null;
    /** The formula as last shown (labels: `[Opportunity.Created date] + 10 days`), never compiled. */
    formula_label?: string | null;
};

/**
 * Where a distance is measured from (`server/crm/filter_tree/geo.py`): a fixed point, or the
 * location of one record, read when the list loads.
 */
export type GeoOrigin =
    | { type: "point"; lat: number; lng: number; label?: string | null }
    | { type: "record"; model: string; id: string | number; point?: string | null; label?: string | null }
    /** A location of the record being filtered (`scope` 0) or of a record around the condition. */
    | { type: "field"; scope: number; point: string; label?: string | null };

/** The value of a `within_km` / `beyond_km` condition, held as the only item of `value`. */
export type GeoValue = { radius_km: number | string | null; origin?: GeoOrigin | null };

export type RelatedNode = {
    type: "related";
    id: string;
    /** Path of to-one relations ending with a to-many relation. */
    relation: string;
    quantifier: Quantifier;
    /** How many related records `at_least` / `fewer_than` count. */
    count?: number | null;
    filter?: GroupNode | null;
};

/** A condition read back from a view saved before the builder, that it cannot edit. */
export type DrqNode = {
    type: "drq";
    id: string;
    query: Record<string, unknown>;
    label?: string | null;
};

export type GroupNode = {
    type: "group";
    id: string;
    conjunction: "and" | "or";
    children: FilterNode[];
};

export type FilterNode = ConditionNode | RelatedNode | DrqNode | GroupNode;

export type FilterTree = {
    version: 1;
    root: GroupNode;
    filters_hash?: string | null;
};

export type SchemaField = {
    name: string;
    label: string;
    type: FilterFieldType;
    operators: FilterOperator[];
    options?: { value: string; label: string }[];
    options_url?: string;
    description?: string;
    /** Operators that can compare the field with a formula (none: no formula on this field). */
    formula_operators?: FilterOperator[];
    /** False when a list cannot sort on it (a filter of the object's config, not a column). */
    sortable?: boolean;
};

export type SchemaRelation = {
    name: string;
    label: string;
    kind: "to_one" | "to_many";
    /** "app_label.model_name" of the target. */
    model: string;
    /** To-one relations are fields too (« Owner is empty »). */
    operators?: FilterOperator[];
    declared?: boolean;
    description?: string;
    /** To-one relations compared as records with a formula (« Owner is ƒx [Account.Owner] »). */
    formula_operators?: FilterOperator[];
};

export type FilterSchema = {
    model: string;
    name: string;
    name_plural: string;
    icon?: string | null;
    color?: string | null;
    object_url?: string | null;
    fields: SchemaField[];
    relations: SchemaRelation[];
};

/** What a path resolves to, hop by hop, for labels and value inputs. */
export type ResolvedPath = {
    /** Labels of the hops, the last one being the field or the related records. */
    labels: string[];
    /** Schema of the model the last segment belongs to. */
    schema: FilterSchema;
    field?: SchemaField;
    relation?: SchemaRelation;
};
